import React from 'react';
import { Box, Typography, Divider, Paper, CircularProgress, Chip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BookIcon from '@mui/icons-material/Book';
import { IntroductionSectionProps } from '../../types/lessonPlan.types';
import StoryDisplay from '../StoryComponents/StoryDisplay';
import ActivityDisplay from '../ActivityComponents/ActivityDisplay';
import StreamingText from '../StreamingComponents/StreamingText';
import StreamingIndicator from '../StreamingComponents/StreamingIndicator';

// Extended props for streaming support  
interface ExtendedIndependentPracticeSectionProps extends IntroductionSectionProps {
  streamingStates?: {
    [key: string]: {
      isActive: boolean;
      progress: number;
      wordsPerMinute?: number;
      estimatedCompletion?: number;
    };
  };
  streamingContent?: {
    [key: string]: string;
  };
  translationRegenerating?: boolean; // Shows loading state when translations are being regenerated
}

/**
 * IndependentPracticeSection Component
 * 
 * Displays the complete independent practice section with real-time streaming support:
 * 1. Practice Story (different from introduction story) - with live streaming
 * 2. Independent Practice Activities - with live streaming
 * 
 * FIXED: Now actually displays content instead of being a placeholder
 */
const IndependentPracticeSection: React.FC<ExtendedIndependentPracticeSectionProps> = ({
  section,
  isStreaming,
  onRegenerate,
  onEdit,
  genre,
  subject,
  streamingStates = {},
  streamingContent = {},
  translationRegenerating = false
}) => {
  
  const practiceStoryStreaming = streamingStates['practice-story'];
  const practiceActivitiesStreaming = streamingStates['practice-activities'];
  const practiceStoryContent = streamingContent['practice-story'] || '';
  const practiceActivitiesContent = streamingContent['practice-activities'] || '';
  
  return (
    <Box>
      {/* Section Header - Removed per user request */}
      {/*
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'secondary.main',
          color: 'secondary.contrastText',
          borderRadius: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <AssignmentIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Independent Practice Section
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Student independent work and skill practice with different story
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <BookIcon />
            <Typography variant="body2">
              Practice Story (different from introduction)
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon />
            <Typography variant="body2">
              Independent Practice Activities
            </Typography>
          </Box>
        </Box>
      </Paper>
      */}

      {/* Practice Story Section */}
      <Box mb={4}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <BookIcon color="secondary" />
          {subject === 'Math' ? 'Additional Concepts & Examples' : 'Practice Story'}
          <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            ({subject === 'Math' ? 'Advanced instruction' : 'Different story for additional practice'})
          </Typography>
        </Typography>
        
        {/* Streaming Indicator for Practice Story */}
        {practiceStoryStreaming?.isActive && (
          <StreamingIndicator
            isActive={practiceStoryStreaming.isActive}
            progress={practiceStoryStreaming.progress}
            contentType="Practice Story"
            estimatedTime={practiceStoryStreaming.estimatedCompletion}
            wordsPerMinute={practiceStoryStreaming.wordsPerMinute}
          />
        )}
        
        {/* Practice Story Content - Use StoryDisplay for all cases */}
        {section.story ? (
          <>
            <StoryDisplay 
              story={section.story}
              isStreaming={isStreaming.story}
              editable
              genre={genre}
              onEdit={(content) => onEdit(content, 'story')}
            />
          </>
        ) : (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              backgroundColor: 'grey.50',
              border: '2px dashed',
              borderColor: 'grey.300'
            }}
          >
            <BookIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Practice Story Not Generated Yet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Click "Generate Practice" or "Generate Lesson Plan" to create the practice story.
            </Typography>
          </Paper>
        )}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Practice Activities Section */}
      <Box>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <AssignmentIcon color="primary" />
          {subject === 'Math' ? 'Additional Practice Problems' : 'Independent Practice Activities'}
          <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
            ({subject === 'Math' ? 'More challenging problems' : 'Post-reading comprehension and skill practice'})
          </Typography>
        </Typography>
        
        {/* Streaming Indicator for Practice Activities */}
        {practiceActivitiesStreaming?.isActive && (
          <StreamingIndicator
            isActive={practiceActivitiesStreaming.isActive}
            progress={practiceActivitiesStreaming.progress}
            contentType="Practice Activities"
            estimatedTime={practiceActivitiesStreaming.estimatedCompletion}
            wordsPerMinute={practiceActivitiesStreaming.wordsPerMinute}
          />
        )}
        
        {/* Practice Activities Content - Show streaming or final content */}
        {practiceActivitiesStreaming?.isActive && practiceActivitiesContent ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <StreamingText
              content={practiceActivitiesContent}
              isStreaming={true}
              showCursor={true}
              animationSpeed={20}
            />
          </Paper>
        ) : isStreaming.activities ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <StreamingText
              content={section.activities || ''}
              isStreaming={true}
              showCursor={true}
              animationSpeed={20}
            />
          </Paper>
        ) : section.activities ? (
          <>
            <ActivityDisplay
              content={section.activities}
              isStreaming={false}
              type="practice"
              onEdit={(content) => onEdit(content, 'activities')}
            />
          </>
        ) : (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              backgroundColor: 'grey.50',
              border: '2px dashed',
              borderColor: 'grey.300'
            }}
          >
            <AssignmentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Practice Activities Not Generated Yet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Click "Generate Practice" or "Generate Lesson Plan" to create the practice activities.
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Translated Independent Practice Section (if available) */}
      {(section.storySpanish || section.activitiesSpanish || translationRegenerating) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            🌐 Translated Content
            {translationRegenerating && <CircularProgress size={20} color="secondary" />}
          </Typography>
          
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2,
              border: '2px solid',
              borderColor: 'secondary.light',
              backgroundColor: 'secondary.50',
              position: 'relative'
            }}
          >
            <Chip 
              label="TRANSLATED VERSION" 
              color="secondary" 
              sx={{ 
                position: 'absolute', 
                top: -10, 
                left: 16, 
                zIndex: 1,
                fontSize: '0.75rem',
                fontWeight: 600
              }} 
            />

            {/* Translated Practice Story and Activities */}
            {translationRegenerating ? (
              <Box mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={40} color="secondary" />
                <Typography variant="body1" sx={{ ml: 2, color: 'text.secondary' }}>
                  Updating translation...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Translated Practice Story Section */}
                {section.storySpanish && (
                  <Box mb={4}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <BookIcon color="secondary" />
                      Practice Story
                      <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                        (Different story for additional practice)
                      </Typography>
                    </Typography>
                    
                    <StoryDisplay 
                      story={section.storySpanish}
                      isStreaming={false}
                      editable={false}
                      genre={genre}
                    />
                  </Box>
                )}

                <Divider sx={{ mb: 4 }} />

                {/* Translated Practice Activities Section */}
                {section.activitiesSpanish && (
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <AssignmentIcon color="primary" />
                      {subject === 'Math' ? 'Additional Practice Problems' : 'Independent Practice Activities'}
                      <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                        ({subject === 'Math' ? 'More challenging problems' : 'Post-reading comprehension and skill practice'})
                      </Typography>
                    </Typography>
                    
                    <ActivityDisplay
                      content={section.activitiesSpanish}
                      isStreaming={false}
                      type="practice"
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      )}

    </Box>
  );
};

export default IndependentPracticeSection;
 